import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useAsyncData } from '#imports'
import { useGraphQL } from './useGraphQL'
import { BREADCRUMB_ENTRIES_QUERY } from '@/queries/breadcrumb-entries'
import { localePrefix } from '@/utils/site-handle'
import { enabledBreadcrumbEntries, type BreadcrumbEntry } from '@/services/jsonld/breadcrumbEntries'

interface Response { hubs?: BreadcrumbEntry[]; categories?: BreadcrumbEntry[] }

/** Share enabled ancestor entries by language; never use a Craft preview token. */
export async function useBreadcrumbEntries(site: MaybeRefOrGetter<string>) {
  const graphql = useGraphQL()
  const key = computed(() => `breadcrumb-entries-${toValue(site)}`)
  const { data } = await useAsyncData(key, async () => {
    const prefix = localePrefix(toValue(site)).replace(/^\//, '')
    if (!prefix) return []
    try {
      const roots = ['blog', 'glossary', 'perspective', 'customers', 'product-release', 'resources', 'ebooks', 'podcasts', 'webinars']
      const hubUris = ['__home__', prefix, ...roots, ...roots.map(root => `${prefix}/${root}`)]
      const result = await graphql.query<Response>(BREADCRUMB_ENTRIES_QUERY, { site: [toValue(site)], hubUris })
      return enabledBreadcrumbEntries([...(result.hubs || []), ...(result.categories || [])], toValue(site))
    } catch {
      // A missing/unavailable CMS response must not invent translated ancestors.
      return []
    }
  }, { default: () => [] as BreadcrumbEntry[] })
  return { breadcrumbEntries: data }
}
